import React, { type FunctionComponent, Fragment, useState } from 'react'
import { css } from '@emotion/react'
import {
  Button,
  CheckboxWithLabel,
  Modal,
  Text,
  fontWeight,
  spacing,
} from '@retailer-platform/shared-components'
import { FormattedMessage } from 'react-intl'
import { useDashMessages } from 'intl/intl.hooks'
import { useDeleteVisibilityCondition } from 'domains/banners/api/deleteVisibilityCondition.hooks'
import { useGoToPath } from 'utils/routing/navigation.hooks'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import LoadingDots from 'legacy/components/LoadingDots'

interface Props {
  visibilityConditionId: string
  visibilityConditionTypeName: string
  placementId: string
  placementName?: string
  isOpen: boolean
  onCancel: () => void
}

const boldStyle = css({
  fontWeight: fontWeight.BOLD,
})

const checkboxWrapper = css({
  marginTop: spacing.X20,
})

const loadingWrapper = css({
  marginTop: spacing.X12,
})

export const VisibilityConditionDeleteModal: FunctionComponent<Props> = ({
  visibilityConditionId,
  placementId,
  visibilityConditionTypeName,
  placementName,
  isOpen,
  onCancel,
}) => {
  const [checked, setChecked] = useState(false)

  const goToBannerDetailsPage = useGoToPath('app-admin-banners-details', {
    placementId: placementId,
  })

  const messages = useDashMessages({
    title: 'bannersDomain.details.visibility-condition.deleteModal.title',
    checkboxLabel: 'bannersDomain.details.visibility-condition.deleteModal.checkboxLabel',
    confirmLabel: 'common.confirm',
    cancelLabel: 'common.cancel',
    error: 'bannersDomain.details.deleteModal.error',
    anonymous: 'bannersDomain.details.anonymous.title',
  })

  const { deleteVisibilityCondition, apiResult, apiError, apiLoading } =
    useDeleteVisibilityCondition()

  const hasError = (apiResult && !apiResult.success) || apiError

  const handleConfirm = () => {
    deleteVisibilityCondition({ id: visibilityConditionId })
  }

  if (apiResult?.success) {
    goToBannerDetailsPage()
  }

  const header = (
    <Text size="large" weight="bold">
      {messages.title}
    </Text>
  )

  const body = (
    <Fragment>
      <FormattedMessage
        id="bannersDomain.details.visibility-condition.deleteModal.body"
        values={{
          conditionName: <b css={boldStyle}>{visibilityConditionTypeName || messages.anonymous}</b>,
          placementName: <b css={boldStyle}>{placementName || messages.anonymous}</b>,
        }}
      />

      <div css={checkboxWrapper}>
        <CheckboxWithLabel
          id="deleteVisibilityConditionModalCheckbox"
          label={messages.checkboxLabel}
          checked={checked}
          onChange={() => setChecked(!checked)}
        />
      </div>

      {hasError && <ErrorMessage>{messages.error}</ErrorMessage>}
      {apiLoading && (
        <div css={loadingWrapper}>
          <LoadingDots height={10} centered />
        </div>
      )}
    </Fragment>
  )

  const footer = (
    <Fragment>
      <Button onClick={onCancel} variant="detrimental">
        {messages.cancelLabel}
      </Button>

      <Button onClick={handleConfirm} variant="primary" disabled={!checked}>
        {messages.confirmLabel}
      </Button>
    </Fragment>
  )

  return <Modal isOpen={isOpen} headerContent={header} bodyContent={body} footerContent={footer} />
}
